// safety/incidents-and-injuries/components/IncidentDetailPage.tsx
import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { StatusPill } from './shared';
import { DETAIL_TABS, DetailTab, fmtDate, todayISO, uid, STATUS_TRANSITIONS, WORKFLOW_STEPS, WORKFLOW_TEMPLATE } from '../utils/incidents-utils';
import type { Incident, IncidentStatus, LetterTemplate, SafetyLists, EmployeeRef, EntityRef } from '../../../../types/incident.types';
import { EditIncidentDrawer } from './EditIncidentDrawer';
import { OverviewTab } from './tabs/OverviewTab';
import { RtwPlanTab } from './tabs/RtwPlanTab';
import { CertificatesTab } from './tabs/CertificatesTab';
import { AppointmentsTab } from './tabs/AppointmentsTab';
import { ExpensesTab } from './tabs/ExpensesTab';
import { WagesTab } from './tabs/WagesTab';
import { NotesTab } from './tabs/NotesTab';
import { InvestigationTab } from './tabs/InvestigationTab';
import { LettersTab } from './tabs/LettersTab';
import FileAttachments from '../../../../components/FileAttachments';
import * as apiSvc from '../../../../utils/safety-incidents-api';

export const IncidentDetailPage: React.FC<{
    incident: Incident;
    lists: SafetyLists;
    employees: EmployeeRef[];
    entities: EntityRef[];
    letterTemplates: LetterTemplate[];
    onBack: () => void;
    onChange: (updated: Incident) => void;
    onSaveTemplate: (t: LetterTemplate) => void;
    onArchiveTemplate: (id: string) => void;
}> = ({ incident, lists, employees, entities, letterTemplates, onBack, onChange, onSaveTemplate, onArchiveTemplate }) => {
    const [tab, setTab] = useState<DetailTab>('Overview');
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const r = incident;

    // Status transition — persisted immediately via the dedicated endpoint
    // (backend enforces the allowed transition map). The 'Incident Closed'
    // workflow step is kept in sync (marked on close, un-marked on reopen).
    const changeStatus = async (status: IncidentStatus) => {
        if (status === r.status || statusUpdating) return;
        setStatusUpdating(true);
        try {
            let updated = await apiSvc.updateIncidentStatus(r.id, status);
            if (status === 'Closed' || r.status === 'Closed') {
                try {
                    updated = await apiSvc.setWorkflowStep(r.id, WORKFLOW_STEPS.CLOSED, {
                        done: status === 'Closed',
                        label: 'Incident Closed',
                        auto: true,
                    });
                } catch { /* status already changed; step sync is best-effort */ }
            }
            onChange(updated);
            toast.success(`Status changed to ${status}`);
        } catch {
            toast.error('Could not change status');
        } finally {
            setStatusUpdating(false);
        }
    };

    // Local mutation helper — updates parent state, then fires the API call.
    // The API call receives the updated draft (never the stale pre-mutation state).
    const mutate = (updater: (draft: Incident) => void, apiCall?: (draft: Incident) => Promise<any>) => {
        const draft: Incident = JSON.parse(JSON.stringify(r));
        updater(draft);
        onChange(draft);
        if (apiCall) apiCall(draft).catch(() => toast.error('Could not save changes'));
    };

    // RTW plan: text edits save debounced (keystroke-level changes); structural
    // changes (add/remove approver) save immediately via handleRtwCommit.
    const rtwSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => {
        if (rtwSaveTimer.current) clearTimeout(rtwSaveTimer.current);
    }, []);

    const handleRtwChange = (rtwPlan: Incident['rtwPlan']) => {
        mutate((d) => { d.rtwPlan = rtwPlan; });
        if (rtwSaveTimer.current) clearTimeout(rtwSaveTimer.current);
        rtwSaveTimer.current = setTimeout(() => {
            apiSvc.saveRtwPlan(r.id, rtwPlan).catch(() => toast.error('Could not save RTW plan'));
        }, 800);
    };

    const handleRtwCommit = (rtwPlan: Incident['rtwPlan']) => {
        if (rtwSaveTimer.current) clearTimeout(rtwSaveTimer.current);
        mutate((d) => { d.rtwPlan = rtwPlan; }, (d) => apiSvc.saveRtwPlan(r.id, d.rtwPlan));
    };

    // Investigation: text edits (breached procedures) save debounced; structural
    // changes (notes, corrective actions, Yes/No flag) save immediately.
    const invSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => {
        if (invSaveTimer.current) clearTimeout(invSaveTimer.current);
    }, []);

    const handleInvestigationChange = (investigation: Incident['investigation']) => {
        mutate((d) => { d.investigation = investigation; });
        if (invSaveTimer.current) clearTimeout(invSaveTimer.current);
        invSaveTimer.current = setTimeout(() => {
            apiSvc.saveInvestigation(r.id, investigation!).catch(() => toast.error('Could not save investigation'));
        }, 800);
    };

    const handleInvestigationCommit = (investigation: Incident['investigation']) => {
        if (invSaveTimer.current) clearTimeout(invSaveTimer.current);
        mutate((d) => { d.investigation = investigation; }, (d) => apiSvc.saveInvestigation(r.id, d.investigation!));
    };

    // Approve one approver. When the last pending approval is granted, the
    // 'RTW Plan Approved' workflow step auto-completes (local + server).
    const handleApprove = (index: number) => {
        const allApproved = r.rtwPlan.approvals.length > 0 &&
            r.rtwPlan.approvals.every((a, j) => j === index || a.state === 'Approved');
        mutate(
            (d) => {
                const a = d.rtwPlan.approvals[index];
                if (a) { a.state = 'Approved'; a.date = todayISO(); }
                if (allApproved) {
                    const s = d.workflow.find((x) => x.step === WORKFLOW_STEPS.RTW_APPROVED);
                    if (s) { s.done = true; s.date = todayISO(); }
                    else d.workflow.push({ step: WORKFLOW_STEPS.RTW_APPROVED, label: 'RTW Plan Approved', who: '', done: true, date: todayISO(), auto: true });
                }
            },
            async (d) => {
                await apiSvc.saveRtwPlan(r.id, d.rtwPlan);
                if (allApproved) {
                    await apiSvc.setWorkflowStep(r.id, WORKFLOW_STEPS.RTW_APPROVED, { done: true, label: 'RTW Plan Approved', auto: true });
                }
            },
        );
    };

    // Core-field edit (drawer) — persisted via the generic update endpoint.
    // Capacity is re-derived from fitToReturn, same as on incident creation.
    const saveEdit = async (values: Partial<Incident>) => {
        try {
            const updated = await apiSvc.updateIncident(r.id, {
                ...values,
                capacity: values.fitToReturn ? 'Light Duties' : 'No Work',
            });
            onChange(updated);
            setEditOpen(false);
            toast.success('Incident updated');
        } catch {
            toast.error('Could not save changes');
        }
    };

    const Actions = (
        <>
            <button onClick={onBack} className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">
                Back
            </button>
            <button onClick={() => setEditOpen(true)} className="h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
                Edit Incident
            </button>
        </>
    );

    return (
        <div className="bg-background min-h-screen pb-10 px-0 sm:px-4 lg:px-0">
            <div className="max-w-5xl mx-auto px-0 sm:px-4 lg:px-0 py-7">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-2">
                            <ChevronLeft size={16} />
                            Incident Register
                        </button>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">{r.incidentNumber}</span>
                            <StatusPill status={r.status} />
                            <select
                                value={r.status}
                                disabled={statusUpdating}
                                onChange={(e) => changeStatus(e.target.value as IncidentStatus)}
                                title="Change status"
                                className="h-7 rounded-lg border border-border bg-card px-2 text-xs font-medium text-muted-foreground hover:bg-accent disabled:opacity-50"
                            >
                                {[r.status, ...(STATUS_TRANSITIONS[r.status] ?? [])].map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                        <h1 className="text-2xl font-medium mt-1">{r.employeeName}</h1>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            {r.entityName} &nbsp;·&nbsp; RTW: {r.rtwCoordinator || '—'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">{Actions}</div>
                </div>

                <div className="flex gap-1.5 overflow-x-auto pb-0 mb-5 border-b border-border">
                    {DETAIL_TABS.map((t) => (
                        <button
                            key={t}
                            onClick={() => setTab(t)}
                            className={`px-3.5 py-2 rounded-lg text-sm whitespace-nowrap ${
                                t === tab ? 'bg-primary text-primary-foreground font-medium' : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                            }`}
                        >
                            {t}
                        </button>
                    ))}
                </div>

                <div>
                    {tab === 'Overview' && (
                        <OverviewTab
                            incident={r}
                            onCompleteStep={(step) => {
                                const def = WORKFLOW_TEMPLATE.find((t) => t.step === step);
                                mutate(
                                    (d) => {
                                        const s = d.workflow.find((x) => x.step === step);
                                        if (s) { s.done = true; s.date = todayISO(); }
                                        else d.workflow.push({ step, label: def?.label ?? `Step ${step}`, who: '', done: true, date: todayISO(), auto: def?.auto ?? false });
                                    },
                                    () => apiSvc.setWorkflowStep(r.id, step, { done: true, label: def?.label, auto: def?.auto }),
                                );
                            }}
                        />
                    )}

                    {tab === 'RTW Plan' && (
                        <RtwPlanTab
                            incident={r}
                            employees={employees}
                            onChange={handleRtwChange}
                            onCommit={handleRtwCommit}
                            onApprove={handleApprove}
                        />
                    )}

                    {tab === 'Certificates' && (
                        <CertificatesTab
                            incident={r}
                            lists={lists}
                            onAdd={(c) => mutate((d) => { d.certificates.unshift(c); }, () => apiSvc.addCertificate(r.id, c))}
                            onDelete={(id) => mutate((d) => { d.certificates = d.certificates.filter((x) => x.id !== id); }, () => apiSvc.deleteCertificate(r.id, id))}
                        />
                    )}

                    {tab === 'Medical Appointments' && (
                        <AppointmentsTab
                            incident={r}
                            lists={lists}
                            onAdd={(a) => { const withId = { ...a, id: uid('appt') }; mutate((d) => { d.appointments.unshift(withId); }, () => apiSvc.addAppointment(r.id, withId)); }}
                            onDelete={(id) => mutate((d) => { d.appointments = d.appointments.filter((x) => x.id !== id); }, () => apiSvc.deleteAppointment(r.id, id))}
                        />
                    )}

                    {tab === 'Medical Expenses' && (
                        <ExpensesTab
                            incident={r}
                            onAdd={(e) => { const withId = { ...e, id: uid('exp') }; mutate((d) => { d.expenses.unshift(withId); }, () => apiSvc.addExpense(r.id, withId)); }}
                            onDelete={(id) => mutate((d) => { d.expenses = d.expenses.filter((x) => x.id !== id); }, () => apiSvc.deleteExpense(r.id, id))}
                        />
                    )}

                    {tab === 'Wage Reimbursements' && (
                        <WagesTab
                            incident={r}
                            onAdd={(w) => { const withId = { ...w, id: uid('wage') }; mutate((d) => { d.wages.unshift(withId); }, () => apiSvc.addWage(r.id, withId)); }}
                            onDelete={(id) => mutate((d) => { d.wages = d.wages.filter((x) => x.id !== id); }, () => apiSvc.deleteWage(r.id, id))}
                        />
                    )}

                    {tab === 'File Notes' && (
                        <NotesTab
                            incident={r}
                            onAdd={(n) => { const withId = { ...n, id: uid('note') }; mutate((d) => { d.notes.unshift(withId); }, () => apiSvc.addNote(r.id, withId)); }}
                            onDelete={(id) => mutate((d) => { d.notes = d.notes.filter((x) => x.id !== id); }, () => apiSvc.deleteNote(r.id, id))}
                        />
                    )}

                    {tab === 'Incident Investigation' && (
                        <InvestigationTab
                            incident={r}
                            employees={employees}
                            onChange={handleInvestigationChange}
                            onCommit={handleInvestigationCommit}
                        />
                    )}

                    {tab === 'Attachments' && (
                        <div className="space-y-5">
                            {/* Attachment endpoints persist immediately — onAttachmentsChange
                                only syncs local state, no extra save call needed. */}
                            <FileAttachments
                                title="Photos"
                                resourceType="safety-incidents"
                                resourceId={String(r.id)}
                                category="photos"
                                attachments={r.photos ?? []}
                                onAttachmentsChange={(next) => mutate((d) => { d.photos = next; })}
                            />
                            <FileAttachments
                                title="Incident Reports"
                                resourceType="safety-incidents"
                                resourceId={String(r.id)}
                                category="incidentReports"
                                attachments={r.incidentReports ?? []}
                                onAttachmentsChange={(next) => mutate((d) => { d.incidentReports = next; })}
                            />
                            <FileAttachments
                                title="Medical Documents"
                                resourceType="safety-incidents"
                                resourceId={String(r.id)}
                                category="medicalDocuments"
                                attachments={r.medicalDocuments ?? []}
                                onAttachmentsChange={(next) => mutate((d) => { d.medicalDocuments = next; })}
                            />
                        </div>
                    )}

                    {tab === 'Letter Templates' && (
                        <LettersTab incident={r} templates={letterTemplates} onSaveTemplate={onSaveTemplate} onArchive={onArchiveTemplate} />
                    )}
                </div>

                <div className="mt-8 flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                        Created by {r.createdBy?.name ?? '—'} · {fmtDate(r.date)} · Last modified {fmtDate(r.updatedAt)}
                    </p>
                    <div className="flex items-center gap-2">{Actions}</div>
                </div>

                <EditIncidentDrawer
                    open={editOpen}
                    incident={r}
                    lists={lists}
                    employees={employees}
                    entities={entities}
                    onClose={() => setEditOpen(false)}
                    onSave={saveEdit}
                />
            </div>
        </div>
    );
};

export default IncidentDetailPage;
